import * as api from '../../api';
import { handleError } from '../actionHelpers';
import ReactGA from 'react-ga';
import { setUIHintForActiveUser } from '../users/actions';
import { getActiveUser } from '../reducers';
import * as amplitudeEvents from './amplitude';
import * as constants from './event-constants';
import { ROUTES } from 'Settings/route-strings';

/* GENERIC EVENTS */

const { GA, API_HINT, API_EVENT, AMPLITUDE } = constants;

// These are events on the website, not in the models.
export const sendAnalyticsEvent = (eventId, services, properties) => (
  dispatch,
  getState
) => {
  if (!eventId || !services || !properties) return;
  if (services.includes(GA)) {
    // props: category, action, value, label
    ReactGA.event({
      ...properties,
    });
  }

  if (services.includes(API_HINT)) {
    dispatch(setUIHintForActiveUser(eventId));
  }

  if (services.includes(API_EVENT)) {
    const app = properties.app || 'web';
    const { token } = getState().users.auth;
    api.postStatsEvent(app, eventId, token);
  }

  if (services.includes(AMPLITUDE)) {
    amplitudeEvents.logWebEvent(
      `${properties.category} - ${properties.action}`,
      {
        ...properties,
        hostname: window.location.hostname,
      }
    );
  }
};

export const sendAnalyticsEventById = (
  eventId,
  action,
  additionalProperties
) => dispatch => {
  if (!Object.keys(constants.propertiesById).includes(eventId))
    throw Error('No matching event ID');

  const properties = {
    ...constants.propertiesById[eventId],
    ...additionalProperties,
  };
  if (action) properties.action = action;
  dispatch(
    sendAnalyticsEvent(eventId, constants.servicesById[eventId], properties)
  );
};

export const viewedModelPage = slug => dispatch => {
  const eventId = 'view_model_page';
  const properties = constants.propertiesById[eventId];
  properties.label = slug;
  properties.app = slug;
  dispatch(
    sendAnalyticsEvent(eventId, constants.servicesById[eventId], properties)
  );
};

export const sendInvitedTeachersEvent = count => dispatch => {
  const eventId = 'invited_teachers';
  const properties = constants.propertiesById[eventId];
  properties.label = count;
  dispatch(
    sendAnalyticsEvent(eventId, constants.servicesById[eventId], properties)
  );
};

export const sendAnalyticsTiming = (
  eventId,
  services,
  properties
) => dispatch => {
  const { category, variable, value, label } = properties;
  if (services.includes(GA)) {
    // props: category, variable, value, label
    ReactGA.timing({ category, variable, value, label });
  }

  if (services.includes(AMPLITUDE)) {
    const amperties = {
      label,
      action: variable,
      seconds: Math.round(properties.value / 1000),
      minutes: Math.round(properties.value / 100 / 60) / 10,
      ...properties.buildSettings,
      hostname: window.location.hostname,
    };
    amplitudeEvents.logWebEvent(
      `${properties.category} - ${variable}`,
      amperties
    );
  }
};

export const sendGameLoadingTime = (props, start, stop) => dispatch => {
  const loadTime = stop - start;
  console.log('Model load time: ', loadTime / 1000);
  dispatch(
    sendAnalyticsTiming('model_load_time', [GA, AMPLITUDE], {
      category: 'Model Page',
      variable: 'load',
      value: loadTime,
      label: props.slug,
      buildSettings: props.gameSettings.buildSettings,
    })
  );
};

export const initializeAmplitudeForWeb = () => (dispatch, getState) => {
  const activeUser = getActiveUser(getState());
  amplitudeEvents.initializeAmplitude('web-client', activeUser);
};

export const setAmplitudeUserForWeb = () => (dispatch, getState) => {
  const activeUser = getActiveUser(getState());
  amplitudeEvents.setUserId('web-client', activeUser.id);
  amplitudeEvents.setUserProperties('web-client', {
    'Institution ID': activeUser.roles[0][0],
    'User Roles': activeUser.roles[0][1],
  });
};

export const fetchStatsByType = statsType => (dispatch, getState) => {
  dispatch({
    type: `FETCH_STATS_REQUEST`,
    statsType,
  });
  const { token } = getState().users.auth;
  return api.getGenericStats(token, `/admin/stats/${statsType}`).then(
    response => {
      if (response.errorMsg) {
        if (response.errorCode === 1001)
          dispatch({
            type: 'ADD_PAGE_ERROR',
            route: ROUTES.ADMIN_STATS,
            error: response,
          });
        handleError(dispatch, response, `FETCH_STATS_FAILURE`, {
          statsType,
        });
      } else {
        dispatch({
          type: `FETCH_STATS_SUCCESS`,
          statsType,
          response,
        });
      }
    },
    error => handleError(dispatch, error, `FETCH_STATS_FAILURE`, { statsType })
  );
};

export const fetchGenericStats = route => (dispatch, getState) => {
  dispatch({
    type: 'FETCH_GENERIC_STATS_REQUEST',
  });
  const { token } = getState().users.auth;
  return api.getGenericStats(token, route).then(
    response => {
      if (response.errorMsg) {
        handleError(dispatch, response, 'FETCH_GENERIC_STATS_FAILURE');
      } else {
        dispatch({
          type: 'FETCH_GENERIC_STATS_SUCCESS',
          response,
          route,
        });
      }
    },
    error => handleError(dispatch, error, 'FETCH_GENERIC_STATS_FAILURE')
  );
};
